import React, { useMemo, useState } from 'react';
import { Button } from '../ui/button';

function formatMinutes(minutes = 0) {
  const safeMinutes = Math.max(0, Number(minutes) || 0);
  const hours = Math.floor(safeMinutes / 60);
  const remainder = safeMinutes % 60;
  if (!hours) return `${remainder}m`;
  if (!remainder) return `${hours}h`;
  return `${hours}h ${String(remainder).padStart(2, '0')}m`;
}

function metricTone(key) {
  const tones = {
    total: 'from-slate-900 via-slate-800 to-slate-700 text-white',
    progress: 'from-blue-600 via-cyan-500 to-teal-400 text-white',
    due: 'from-amber-500 via-orange-500 to-rose-500 text-white',
    effort: 'from-emerald-500 via-green-500 to-lime-400 text-slate-950',
  };
  return tones[key] || tones.total;
}

function barTone(statusKey) {
  if (statusKey === 'feito') return 'bg-emerald-500';
  if (statusKey === 'em_progresso') return 'bg-blue-500';
  if (statusKey === 'parado') return 'bg-rose-500';
  return 'bg-slate-400';
}

function statusPillTone(statusKey) {
  if (statusKey === 'feito') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (statusKey === 'em_progresso') return 'border-blue-200 bg-blue-50 text-blue-700';
  if (statusKey === 'parado') return 'border-rose-200 bg-rose-50 text-rose-700';
  return 'border-slate-200 bg-slate-100 text-slate-600';
}

function StatCard({ label, value, detail, toneKey }) {
  return (
    <article className={`rounded-2xl bg-gradient-to-br p-5 shadow-sm ${metricTone(toneKey)}`}>
      <p className="text-xs font-semibold uppercase tracking-[0.18em] opacity-80">{label}</p>
      <p className="mt-4 text-3xl font-bold tracking-tight">{value}</p>
      <p className="mt-2 text-sm opacity-80">{detail}</p>
    </article>
  );
}

export default function ProjectDashboard({ board, insights, onRefresh }) {
  const [refreshing, setRefreshing] = useState(false);
  const summary = insights?.summary;

  const riskItems = useMemo(() => {
    const groups = insights?.groups || [];
    return groups
      .flatMap(group => (group.items || []).map(item => ({
        ...item,
        group_name: group.name,
        risk_score:
          (item.rollup?.overdue_subtasks || 0) * 3 +
          (item.rollup?.blocked_subtasks || 0) * 2 +
          ((item.rollup?.is_overdue || item.rollup?.risk_level === 'high') ? 1 : 0),
      })))
      .filter(item => item.risk_score > 0)
      .sort((left, right) => right.risk_score - left.risk_score)
      .slice(0, 6);
  }, [insights]);

  if (!board) {
    return <div className="rounded-2xl border border-slate-200 bg-white p-8 text-sm text-slate-500">Selecione um quadro para visualizar o dashboard.</div>;
  }

  if (!insights || !summary) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">
        Carregando visão consolidada do board...
      </div>
    );
  }

  const handleRefreshClick = async () => {
    if (!onRefresh || refreshing) return;
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <section className="min-h-[calc(100vh-190px)] px-3 py-4 sm:px-5 lg:px-7">
      <div className="mx-auto w-full max-w-none space-y-6">
        <header className="rounded-3xl border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.14),_transparent_34%),linear-gradient(135deg,_#0f172a,_#1e293b_58%,_#334155)] px-6 py-6 text-white shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-200">Board Intelligence</p>
              <h2 className="mt-2 text-3xl font-bold tracking-tight">{insights.board_name || board.name}</h2>
              <p className="mt-2 max-w-3xl text-sm text-slate-300">
                Dashboard operacional consolidado com tarefas, subtarefas, esforço planejado e carga por responsável.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-300">Subtarefas</p>
                <p className="mt-1 text-xl font-semibold">{summary.subtasks}</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-300">Carga planejada</p>
                <p className="mt-1 text-xl font-semibold">{formatMinutes(summary.planned_minutes)}</p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={handleRefreshClick}
                disabled={refreshing}
                className="border-white/20 bg-white/10 text-white hover:bg-white/20 hover:text-white"
              >
                {refreshing ? 'Atualizando...' : 'Atualizar visão'}
              </Button>
            </div>
          </div>
        </header>

        <div className="grid gap-4 lg:grid-cols-4">
          <StatCard
            label="Escopo total"
            value={summary.total_items}
            detail={`${summary.primary_tasks} tarefas pai + ${summary.subtasks} subtarefas`}
            toneKey="total"
          />
          <StatCard
            label="Progresso real"
            value={`${summary.progress_percentage}%`}
            detail={`${summary.completed_items} concluídos · ${summary.in_progress_items} em execução`}
            toneKey="progress"
          />
          <StatCard
            label="Pressão de prazo"
            value={summary.overdue_items}
            detail={`${insights.due_buckets?.today || 0} vencem hoje · ${insights.due_buckets?.this_week || 0} ainda nesta semana`}
            toneKey="due"
          />
          <StatCard
            label="Esforço realizado"
            value={formatMinutes(summary.tracked_minutes)}
            detail={`${formatMinutes(summary.planned_minutes)} planejados · ${summary.unassigned_items} sem responsável`}
            toneKey="effort"
          />
        </div>

        <div className="grid gap-6 xl:grid-cols-[1.35fr_1fr]">
          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Progresso por grupo</p>
                <h3 className="mt-1 text-xl font-semibold text-slate-900">Onde o board está andando e onde travou</h3>
              </div>
              <div className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600">
                {insights.group_progress?.length || 0} grupos
              </div>
            </div>

            <div className="mt-5 space-y-4">
              {(insights.group_progress || []).map(group => (
                <article key={group.id} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <div className="flex items-center gap-3">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: group.color || '#3b82f6' }} />
                        <h4 className="font-semibold text-slate-900">{group.name}</h4>
                      </div>
                      <p className="mt-1 text-sm text-slate-500">
                        {group.total_items} itens · {group.primary_tasks} tarefas pai · {group.subtasks} subtarefas
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
                      <div className="rounded-xl bg-white px-3 py-2 text-center">
                        <p className="text-[11px] uppercase tracking-wide text-slate-400">Progresso</p>
                        <p className="mt-1 font-semibold text-slate-900">{group.progress_percentage}%</p>
                      </div>
                      <div className="rounded-xl bg-white px-3 py-2 text-center">
                        <p className="text-[11px] uppercase tracking-wide text-slate-400">Bloqueios</p>
                        <p className="mt-1 font-semibold text-rose-600">{group.blocked_items}</p>
                      </div>
                      <div className="rounded-xl bg-white px-3 py-2 text-center">
                        <p className="text-[11px] uppercase tracking-wide text-slate-400">Atrasos</p>
                        <p className="mt-1 font-semibold text-amber-600">{group.overdue_items}</p>
                      </div>
                      <div className="rounded-xl bg-white px-3 py-2 text-center">
                        <p className="text-[11px] uppercase tracking-wide text-slate-400">Esforço</p>
                        <p className="mt-1 font-semibold text-emerald-700">{formatMinutes(group.planned_minutes)}</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-200">
                    <div className="h-full rounded-full bg-blue-600" style={{ width: `${group.progress_percentage}%` }} />
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Carga por responsável</p>
              <h3 className="mt-1 text-xl font-semibold text-slate-900">Quem está puxando mais esforço no board</h3>
            </div>

            <div className="mt-5 space-y-3">
              {(insights.assignee_workload || []).slice(0, 8).map(assignee => (
                <article key={assignee.id} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-3">
                        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-sm font-bold text-white">
                          {assignee.name?.charAt(0)?.toUpperCase() || 'U'}
                        </span>
                        <div>
                          <p className="truncate font-semibold text-slate-900">{assignee.name}</p>
                          <p className="text-xs text-slate-500">
                            {assignee.count} itens · {assignee.subtasks} subtarefas · {assignee.completion_percentage}% concluído
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="text-right text-sm">
                      <p className="font-semibold text-slate-900">{formatMinutes(assignee.planned_minutes)}</p>
                      <p className="text-xs text-slate-500">planejado</p>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs">
                    <div className="rounded-xl bg-white px-2 py-2">
                      <p className="text-slate-400">Rastreado</p>
                      <p className="mt-1 font-semibold text-emerald-700">{formatMinutes(assignee.tracked_minutes)}</p>
                    </div>
                    <div className="rounded-xl bg-white px-2 py-2">
                      <p className="text-slate-400">Atrasados</p>
                      <p className="mt-1 font-semibold text-amber-700">{assignee.overdue_items}</p>
                    </div>
                    <div className="rounded-xl bg-white px-2 py-2">
                      <p className="text-slate-400">Participação</p>
                      <p className="mt-1 font-semibold text-slate-900">{assignee.percentage}%</p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </div>

        <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Risco operacional</p>
                <h3 className="mt-1 text-xl font-semibold text-slate-900">Tarefas pai que pedem ação imediata</h3>
              </div>
              <div className="rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-medium text-rose-700">
                {riskItems.length} em destaque
              </div>
            </div>

            <div className="mt-5 space-y-3">
              {riskItems.length === 0 && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-5 text-sm text-emerald-700">
                  Nenhuma tarefa pai com risco operacional relevante neste momento.
                </div>
              )}

              {riskItems.map(item => (
                <article key={item.id} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{item.group_name}</p>
                      <h4 className="mt-1 truncate text-base font-semibold text-slate-900">{item.name}</h4>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className={`rounded-full border px-3 py-1 text-xs font-medium ${statusPillTone(item.rollup?.status_key)}`}>
                          {item.rollup?.status_label}
                        </span>
                        {(item.rollup?.overdue_subtasks || 0) > 0 && (
                          <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">
                            {item.rollup.overdue_subtasks} subtarefas atrasadas
                          </span>
                        )}
                        {(item.rollup?.blocked_subtasks || 0) > 0 && (
                          <span className="rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-medium text-rose-700">
                            {item.rollup.blocked_subtasks} bloqueios
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-center text-xs sm:grid-cols-3">
                      <div className="rounded-xl bg-white px-3 py-2">
                        <p className="text-slate-400">Subtarefas</p>
                        <p className="mt-1 font-semibold text-slate-900">
                          {item.rollup?.subtasks_completed || 0}/{item.rollup?.subtasks_total || 0}
                        </p>
                      </div>
                      <div className="rounded-xl bg-white px-3 py-2">
                        <p className="text-slate-400">Planejado</p>
                        <p className="mt-1 font-semibold text-slate-900">{formatMinutes(item.rollup?.planned_minutes)}</p>
                      </div>
                      <div className="rounded-xl bg-white px-3 py-2">
                        <p className="text-slate-400">Próximo prazo</p>
                        <p className="mt-1 font-semibold text-slate-900">
                          {item.rollup?.next_due_date ? new Date(`${item.rollup.next_due_date}T12:00:00`).toLocaleDateString('pt-BR') : 'Sem prazo'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-200">
                    <div className="h-full rounded-full bg-blue-600" style={{ width: `${item.rollup?.completion_percentage || 0}%` }} />
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Distribuição consolidada</p>
              <h3 className="mt-1 text-xl font-semibold text-slate-900">Status e prioridades do board</h3>
            </div>

            <div className="mt-5 space-y-5">
              <div>
                <p className="mb-3 text-sm font-medium text-slate-700">Status</p>
                <div className="space-y-3">
                  {(insights.status_distribution || []).map(status => (
                    <div key={status.key} className="flex items-center gap-3">
                      <div className="w-28 text-sm text-slate-600">{status.name}</div>
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-200">
                        <div className={`h-full rounded-full ${barTone(status.key)}`} style={{ width: `${status.percentage}%` }} />
                      </div>
                      <div className="w-20 text-right text-sm font-medium text-slate-700">
                        {status.count} · {status.percentage}%
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="mb-3 text-sm font-medium text-slate-700">Prioridades</p>
                <div className="grid grid-cols-2 gap-2">
                  {(insights.priority_distribution || []).map(priority => (
                    <div key={priority.key} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{priority.name}</p>
                      <p className="mt-2 text-xl font-semibold text-slate-900">{priority.count}</p>
                      <p className="text-xs text-slate-500">{priority.percentage}% do board</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="mb-3 text-sm font-medium text-slate-700">Janela de prazo</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: 'Atrasados', value: insights.due_buckets?.overdue || 0, tone: 'text-rose-700 border-rose-200 bg-rose-50' },
                    { label: 'Hoje', value: insights.due_buckets?.today || 0, tone: 'text-amber-700 border-amber-200 bg-amber-50' },
                    { label: 'Esta semana', value: insights.due_buckets?.this_week || 0, tone: 'text-blue-700 border-blue-200 bg-blue-50' },
                    { label: 'Sem prazo', value: insights.due_buckets?.without_due_date || 0, tone: 'text-slate-700 border-slate-200 bg-slate-50' },
                  ].map(bucket => (
                    <div key={bucket.label} className={`rounded-2xl border px-4 py-3 ${bucket.tone}`}>
                      <p className="text-xs font-semibold uppercase tracking-wide">{bucket.label}</p>
                      <p className="mt-2 text-xl font-semibold">{bucket.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </section>
  );
}
